"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Clock, PhoneCall, ShieldCheck, Navigation, CircleCheck as CheckCircle2, ArrowRight } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const neighborhoods = [{
  name: "Downtown Jonesboro & Historic District",
  zip: "30236",
  eta: "20-30 min",
  highlights: "Historic homes, commercial mains, burst pipe emergency response"
}, {
  name: "Lake Spivey & Country Club Estates",
  zip: "30236",
  eta: "25-35 min",
  highlights: "Water heater failure, sewer backup line clearing, pressure issues"
}, {
  name: "Tara Boulevard & Commercial Corridor",
  zip: "30238",
  eta: "15-25 min",
  highlights: "Commercial grease traps, rapid dispatch drain unclogging"
}, {
  name: "Irondale & Flint River Road",
  zip: "30238",
  eta: "20-35 min",
  highlights: "Slab leak detection, emergency shutoff assistance, pipe repair"
}, {
  name: "Morrow & Southlake Area Border",
  zip: "30260",
  eta: "25-40 min",
  highlights: "Main water line breaks, toilet overflow & sump pump service"
}, {
  name: "Riverdale & Clayton Borderline",
  zip: "30274",
  eta: "25-40 min",
  highlights: "24/7 night & weekend emergency plumbing dispatches"
}];
const zipCodes = ["30236", "30237", "30238", "30260", "30274", "30297"];
export default function ServiceArea() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="serviceareacoverage" data-nav="light" className="relative bg-background py-16 md:py-24 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center">
          <Badge variant="secondary" className="mb-4 inline-flex items-center gap-1.5 px-3 py-1 font-semibold text-secondary-foreground">
            <MapPin className="size-3.5 text-accent" />
            Clayton County & Local Coverage
          </Badge>

          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            Jonesboro, GA Emergency Plumbing Coverage
          </h2>

          <p className="font-sans mt-4 text-base text-muted-foreground sm:text-lg">
            Our master technicians are stationed across Clayton County to guarantee
            rapid response times for active floods, burst pipes, water heater leaks, and
            urgent sewer backups.
          </p>
        </div>

        {/* Highlight Banner / Dispatch Stats */}
        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="flex items-center gap-4 rounded-xl border border-border bg-card p-5 shadow-sm">
            <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Clock className="size-6 text-primary" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Average Arrival Time
              </p>
              <p className="text-xl font-bold text-card-foreground">
                30-45 Minutes
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4 rounded-xl border border-border bg-card p-5 shadow-sm">
            <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-accent/20 text-accent-foreground">
              <ShieldCheck className="size-6 text-accent-foreground" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Availability
              </p>
              <p className="text-xl font-bold text-card-foreground">
                24/7/365 Local Dispatch
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4 rounded-xl border border-border bg-card p-5 shadow-sm sm:col-span-2 lg:col-span-1">
            <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Navigation className="size-6 text-primary" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Service Radius
              </p>
              <p className="text-xl font-bold text-card-foreground">
                Jonesboro & 15-Mile Radius
              </p>
            </div>
          </div>
        </div>

        {/* Main Content: Map Visual / Context & Neighborhood Grid */}
        <div className="mt-12 grid grid-cols-1 items-start gap-8 lg:grid-cols-12">
          {/* Left Column: Local Emergency Overview & Contact Action */}
          <div className="flex flex-col gap-6 lg:col-span-5">
            <Card className="border border-border bg-card shadow-sm">
              <CardHeader>
                <CardTitle className="font-default text-xl font-bold text-card-foreground">
                  Ready for Immediate Local Dispatch
                </CardTitle>
                <CardDescription className="text-muted-foreground">
                  Plumbing crises require immediate intervention. We operate fully equipped
                  mobile service units ready with industrial drain snakes, pipe replacement
                  hardware, and high-capacity pump systems.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2.5">
                  <div className="flex items-start gap-2 text-sm text-card-foreground">
                    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-accent" />
                    <span>No extra charge for late-night or holiday dispatches</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-card-foreground">
                    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-accent" />
                    <span>Direct phone connection with an on-call licensed plumber</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-card-foreground">
                    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-accent" />
                    <span>Fully licensed, bonded, and insured in the state of Georgia</span>
                  </div>
                </div>

                <div className="rounded-lg bg-muted/60 p-4">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Primary Clayton County Zip Codes
                  </h4>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {zipCodes.map((zip, i) => <span key={zip} data-index={i} className="inline-flex items-center rounded-md border border-border bg-background px-2.5 py-1 text-xs font-semibold text-foreground">
                        {zip}
                      </span>)}
                  </div>
                </div>

                <div className="flex flex-col gap-3 pt-2 sm:flex-row">
                  <Button asChild size="lg" className="w-full bg-accent text-accent-foreground shadow-sm hover:bg-accent/90">
                    <a href="tel:4708479076" className="inline-flex items-center justify-center gap-2">
                      <PhoneCall className="size-4" />
                      <span>Call (470) 847-9076</span>
                    </a>
                  </Button>

                  <Button asChild variant="outline" size="lg" className="w-full border-border hover:bg-accent hover:text-accent-foreground">
                    <Link to="/contact/" className="inline-flex items-center justify-center gap-2">
                      <span>Request Service</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column: Key Areas & Neighborhoods Grid */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.1
        }} className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:col-span-7">
            {neighborhoods.map((area, i) => <motion.div key={area.name} data-index={i} variants={itemVariants} className="group flex flex-col justify-between rounded-xl border border-border bg-card p-5 shadow-sm transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-md">
                <div>
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-mono inline-flex items-center rounded-md bg-secondary px-2 py-0.5 text-xs font-semibold text-secondary-foreground">
                      ZIP {area.zip}
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-accent-foreground bg-accent/20 px-2 py-0.5 rounded-md">
                      <Clock className="size-3" />
                      {area.eta}
                    </span>
                  </div>

                  <h3 className="font-default mt-3 text-base font-bold text-card-foreground">
                    {area.name}
                  </h3>

                  <p className="font-sans mt-2 text-xs leading-relaxed text-muted-foreground">
                    {area.highlights}
                  </p>
                </div>

                <div className="mt-4 pt-3 border-t border-border/60 flex items-center justify-between text-xs font-medium text-muted-foreground">
                  <span>Jonesboro Base</span>
                  <span className="text-primary font-semibold group-hover:underline">
                    Fast Truck Dispatch
                  </span>
                </div>
              </motion.div>)}
          </motion.div>
        </div>

        {/* Footer Support Notice */}
        <div className="mt-12 rounded-xl border border-border bg-muted/40 p-6 text-center">
          <p className="text-sm text-muted-foreground">
            {"Outside of the central Jonesboro area? We regularly serve surrounding parts of Clayton, Henry, and Fayette counties. "}
            <Link to="/service-areas/" className="font-semibold text-primary underline underline-offset-4 hover:text-primary/80">
              View our full regional coverage map and service list
            </Link>
          </p>
        </div>
      </div>
    </section>;
}